import { Legislation } from "@/models/legislation/Legislation";
import {
  InboundChange,
  OutboundChange,
} from "@/models/legislation/metadata/ConsolidationMetadata";
import { RetsinformationMetadata } from "@/models/legislation/metadata/Retsinformation";
import { DataSource, normalizeDataSource } from "@/config/datasources";
import { getRetsinformationMetadataFromLegislation } from "@/lib/consolidationLegislationUtils";
import { isRetsinformationMetadata } from "@/utils/legislationMetadataUtils";

/** Anchor LBK used for local consolidation smoke tests (LAR-278). */
export const CONSOLIDATION_DEV_ANCHOR_ACCESSION = "A20220033729";

/** Changelaw consolidated into the dev anchor (LAR-278). */
export const CONSOLIDATION_DEV_CHANGELAW_ACCESSION = "A20220057030";

export type ConsolidationDevMetadataOverride = Pick<
  RetsinformationMetadata,
  "inboundChanges" | "outboundChanges"
>;

const anchorInboundChanges: InboundChange[] = [
  {
    changelawAccession: CONSOLIDATION_DEV_CHANGELAW_ACCESSION,
    status: "APPROVED",
    timelineIndex: 10,
    approvedAt: "2022-06-01T00:00:00Z",
  },
  {
    changelawAccession: "A20220099999",
    status: "NEED_REVIEW",
    timelineIndex: 11,
  },
];

const changelawOutboundChanges: OutboundChange[] = [
  {
    anchorLawAccession: CONSOLIDATION_DEV_ANCHOR_ACCESSION,
    status: "APPROVED",
    approvedAt: "2022-06-01T00:00:00Z",
  },
  {
    anchorLawAccession: "A20220088888",
    status: "NEED_REVIEW",
  },
];

/** Dev-only consolidation metadata keyed by Retsinformation accession number. */
export const CONSOLIDATION_DEV_METADATA_BY_EXTERNAL_ID: Readonly<
  Record<string, ConsolidationDevMetadataOverride>
> = {
  [CONSOLIDATION_DEV_ANCHOR_ACCESSION]: {
    inboundChanges: anchorInboundChanges,
  },
  [CONSOLIDATION_DEV_CHANGELAW_ACCESSION]: {
    outboundChanges: changelawOutboundChanges,
  },
};

export const isConsolidationDevMocksEnabled = (): boolean =>
  import.meta.env.DEV &&
  (import.meta.env.VITE_CONSOLIDATION_DEV_MOCKS === "true" ||
    import.meta.env.VITE_CONSOLIDATION_DEV_MOCKS === "1");

export const getConsolidationDevMetadataOverride = (
  externalId: string
): ConsolidationDevMetadataOverride | null => {
  if (!isConsolidationDevMocksEnabled()) return null;
  return CONSOLIDATION_DEV_METADATA_BY_EXTERNAL_ID[externalId] ?? null;
};

/**
 * Merges dev consolidation metadata into legislation fetched from silver.
 * Requires `VITE_CONSOLIDATION_DEV_MOCKS=true` in `.env.local` (also enables consolidation UI locally without Unleash).
 */
export const applyConsolidationDevMetadata = (
  legislation: Legislation
): Legislation => {
  const override = getConsolidationDevMetadataOverride(legislation.externalId);
  if (!override) return legislation;

  if (normalizeDataSource(legislation.datasource) !== DataSource.Retsinformation) {
    return legislation;
  }

  const resolvedMeta = getRetsinformationMetadataFromLegislation(legislation);
  const hasUnusableMetadata =
    legislation.metadata != null && !isRetsinformationMetadata(legislation.metadata);

  if (!resolvedMeta && hasUnusableMetadata && !legislation.metadataObject) {
    return legislation;
  }

  const baseMeta: RetsinformationMetadata = {
    accessionNumber: legislation.externalId,
    documentTypeId: "30",
    retsinfoKlassifikationId: 0,
    ...resolvedMeta,
  };

  const mergedMetadata: RetsinformationMetadata = {
    ...baseMeta,
    ...override,
  };

  if (import.meta.env.DEV) {
    console.info(
      "[consolidation-dev-mocks] Applied metadata override for",
      legislation.externalId
    );
  }

  return {
    ...legislation,
    metadata: mergedMetadata,
  };
};
